import { collection, doc, getDoc, getDocs, onSnapshot, serverTimestamp, writeBatch } from 'firebase/firestore';
import type { Booking, Position, SharedDay, SharedDayMember, SharedMemberData, TimesheetEntry } from '@gigdriver/domain';
import { callable } from './functions';
import { getFirestoreInstance } from './firestore';
import { fromFirestore } from './serde';
import { getGigCalls } from './gigs';

// Shared days (Planning/SharedDay.md): one person's bookings on several gigs,
// priced as ONE person-day. The link record is written only by Cloud Functions;
// everything that mutates it here is a callable. Sending and answering the
// combined offer are single atomic batches across the members' booking docs
// (the existing per-booking rules already permit each write).

export interface LinkSharedDayInput {
  orgId: string;
  members: SharedDayMember[];
  primaryBookingId?: string;
  // The one rate for the day; defaults to the primary booking's own rate.
  rate?: { amount: number; currency?: string };
}

export const linkSharedDay = callable<LinkSharedDayInput, { sharedDayId: string }>('linkSharedDay');

export const unlinkSharedDay = callable<{ orgId: string; sharedDayId: string }, { sharedDayId: string }>('unlinkSharedDay');

// Every shared day in the org, live (coordinator+).
export function watchSharedDays(
  orgId: string,
  onData: (days: SharedDay[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  return onSnapshot(
    collection(getFirestoreInstance(), 'organizations', orgId, 'sharedDays'),
    snap => onData(snap.docs.map(d => fromFirestore<SharedDay>({ id: d.id, ...d.data() }))),
    err => onError?.(err),
  );
}

function bookingRef(orgId: string, m: { gigId: string; positionId: string; id: string }) {
  return doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', m.gigId, 'positions', m.positionId, 'bookings', m.id);
}

function assertSameShare(members: Booking[]): string {
  if (members.length < 2) throw new Error('A shared day needs at least two bookings.');
  const id = members[0].sharedDayId;
  if (!id || members.some(m => m.sharedDayId !== id)) throw new Error('These bookings are not on the same shared day.');
  return id;
}

// Moves every member DRAFT -> OFFERED in one batch, so the person gets ONE
// combined offer or none. (The primary booking's trigger sends the one email.)
export async function sendSharedOffer(members: Booking[]): Promise<void> {
  assertSameShare(members);
  const notDraft = members.find(m => m.state !== 'DRAFT');
  if (notDraft) throw new Error(`Every booking on a shared day must be a draft to send the offer (one is ${notDraft.state}).`);
  const batch = writeBatch(getFirestoreInstance());
  for (const m of members) batch.update(bookingRef(m.orgId, m), { state: 'OFFERED', updatedAt: serverTimestamp() });
  await batch.commit();
}

// Accepts or declines the combined offer: every member in one atomic batch.
// `members` are the person's own bookings (as listed in My Offers); the caller
// has them fresh, and the batch fails (nothing written) if any member's rules
// check no longer holds — e.g. one was cancelled and the day was dissolved.
export async function respondToSharedOffer(members: Booking[], accept: boolean): Promise<void> {
  assertSameShare(members);
  const stale = members.find(m => m.state !== 'OFFERED');
  if (stale) throw new Error('This offer has changed. Refresh and try again.');
  const update = accept
    ? { state: 'ACCEPTED' as const, acceptedAt: serverTimestamp(), updatedAt: serverTimestamp() }
    : { state: 'DECLINED' as const, updatedAt: serverTimestamp() };
  const batch = writeBatch(getFirestoreInstance());
  for (const m of members) batch.update(bookingRef(m.orgId, m), update);
  await batch.commit();
}

// One member's booking, position, calls and timesheet entries — everything the
// shared-day pricing needs. Used for the gigs a page isn't itself showing
// (coordinator+, since it reads timesheet entries). Null if the booking or its
// position is gone.
export async function loadSharedMemberData(orgId: string, m: SharedDayMember): Promise<SharedMemberData | null> {
  const db = getFirestoreInstance();
  const gigPath = ['organizations', orgId, 'gigs', m.gigId] as const;
  const [bookingSnap, positionSnap, calls, entrySnap] = await Promise.all([
    getDoc(doc(db, ...gigPath, 'positions', m.positionId, 'bookings', m.bookingId)),
    getDoc(doc(db, ...gigPath, 'positions', m.positionId)),
    getGigCalls(orgId, m.gigId),
    getDocs(collection(db, ...gigPath, 'timesheetEntries')),
  ]);
  if (!bookingSnap.exists() || !positionSnap.exists()) return null;
  return {
    booking: fromFirestore<Booking>(bookingSnap.data()),
    position: fromFirestore<Position>(positionSnap.data()),
    calls,
    entries: entrySnap.docs.map(d => fromFirestore<TimesheetEntry>(d.data())),
  };
}

// Live updates for one member's booking doc (so a page showing a shared day
// sees the other gigs' bookings move to OFFERED/ACCEPTED/CANCELLED as they happen).
export function watchSharedMemberBooking(
  orgId: string,
  m: SharedDayMember,
  onData: (booking: Booking | null) => void,
  onError?: (err: unknown) => void,
): () => void {
  const ref = doc(getFirestoreInstance(), 'organizations', orgId, 'gigs', m.gigId, 'positions', m.positionId, 'bookings', m.bookingId);
  return onSnapshot(ref, snap => onData(snap.exists() ? fromFirestore<Booking>(snap.data()) : null), err => onError?.(err));
}
